/**
 * REGISTRO de primitivas de captura (deuda D5b de la auditoría C3).
 *
 * Mapa explícito `primitiva → { componente de captura, componente de
 * proyección, textos }`. Bolsillo y Pantalla NO ramifican por tipo: piden la
 * primitiva al registro y renderizan lo que este entrega. Sumar una primitiva
 * nueva (p. ej. `texto_corto` para la nube de palabras) es escribir sus dos
 * componentes y registrarlos acá — sin tocar ninguna página.
 *
 * El enum de tipos válidos vive en {@link esTipoCaptura} (lib/sesion.ts): lo
 * que no está en el registro se muestra como estado "no soportada" visible
 * (D5d), nunca como render en blanco.
 */
import type { ComponentType } from 'react';
import type { Aporte, AporteTipo, CapturaNormal } from '../../lib/sesion';
import { CapturaTexto } from './CapturaTexto';
import { CapturaVoto } from './CapturaVoto';
import { CapturaPosicion } from './CapturaPosicion';
import { CapturaConexion } from './CapturaConexion';
import { CapturaFoto } from './CapturaFoto';
import { ProyeccionPosicion, ProyeccionVoto } from './proyecciones';

/** Contrato de todo componente de CAPTURA (superficie Bolsillo). */
export interface PropsCaptura {
  captura: CapturaNormal;
  /** True mientras la actividad está pausada (bloquea el envío). */
  deshabilitado: boolean;
  /** True mientras el aporte viaja (spinner del botón). */
  enviando: boolean;
  /**
   * Entrega el PAYLOAD completo del aporte (la convención de claves del jsonb
   * es de la primitiva: {texto}, {opcion}, {valor} o los campos que declare el
   * `payload_schema`). La página solo agrega `idem` y lo envía.
   */
  onEnviar: (payload: Record<string, unknown>) => void;
  /** Aportes PROPIOS ya enviados en esta actividad (presupuestos, marcas de «ya votaste»). */
  misAportes?: Aporte[];
  /**
   * Sección activa de la ronda dirigida (actividad.ronda, ADR-0005 §2): la
   * captura con secciones muestra SOLO ese paso y el payload la registra.
   * null/ausente = wizard completo a ritmo propio.
   */
  ronda?: string | null;
  /**
   * Id de la actividad: lo necesitan las primitivas que NO postean por
   * `onEnviar` (la foto sube multipart por el endpoint de media).
   */
  actividadId?: string;
  /**
   * Contador de envíos ACEPTADOS en modo ráfaga (capturas de texto sin
   * secciones): la página lo incrementa en vez de mostrar la confirmación de
   * pantalla completa, y la captura limpia SOLO el texto — las casillas
   * elegidas quedan, para aportar varias veces seguidas sin re-marcar.
   */
  selloEnvio?: number;
}

/** Contrato de todo componente de PROYECCIÓN (superficie Pantalla, C7: solo agregados). */
export interface PropsProyeccion {
  aportes: Aporte[];
  total: number;
}

export interface Primitiva {
  Captura: ComponentType<PropsCaptura>;
  /** Proyección específica en Pantalla; null → solo los agregados genéricos. */
  Proyeccion: ComponentType<PropsProyeccion> | null;
  /** Instrucción bajo el título del Bolsillo (la `ui.ayuda` del manifiesto la reemplaza). */
  instruccion: string;
  /** Texto del botón para participar de nuevo tras la confirmación. */
  otraVez: string;
}

export const PRIMITIVAS: Record<AporteTipo, Primitiva> = {
  texto: {
    Captura: CapturaTexto,
    Proyeccion: null,
    instruccion: 'Escribí tu aporte. Cada idea queda registrada en el acta.',
    otraVez: 'Enviar otra idea',
  },
  voto: {
    Captura: CapturaVoto,
    Proyeccion: ProyeccionVoto,
    instruccion: 'Elegí tu opción. Podés cambiarla mientras la actividad siga abierta.',
    otraVez: 'Cambiar mi respuesta',
  },
  posicion: {
    Captura: CapturaPosicion,
    Proyeccion: ProyeccionPosicion,
    instruccion: 'Marcá tu posición en la escala.',
    otraVez: 'Cambiar mi respuesta',
  },
  conexion: {
    Captura: CapturaConexion,
    Proyeccion: null,
    instruccion: 'Mirá cada tarjeta y decidí en qué grupo va.',
    otraVez: 'Clasificar la siguiente tarjeta',
  },
  foto: {
    Captura: CapturaFoto,
    Proyeccion: null,
    instruccion: 'Tomá la foto y completá su ficha: viajan juntas al acta.',
    otraVez: 'Subir otra foto',
  },
};

/** Primitiva registrada para un tipo YA validado por {@link esTipoCaptura}. */
export function primitivaDe(tipo: AporteTipo): Primitiva {
  return PRIMITIVAS[tipo];
}
